// Menu Assistência (2026-10-16). Chave "assistance-center" fica fora da
// persistência do React Query (__root.tsx) — dado da operação, sempre fresco.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  gpsDriveAssistanceCenterService as api,
  type ImportClientRow,
  type RequestStatus,
  type TowTruckInput,
  type UpdateRequestInput,
} from "@/services/gpsdrive-assistance-center.service";

const KEY = "assistance-center";

export const useAssistanceCenterAccess = (enabled = true) =>
  useQuery({ queryKey: [KEY, "access"], queryFn: api.access, enabled, staleTime: 60_000, retry: 1 });

export const useAssistanceSummary = (from: string, to: string, enabled = true) =>
  useQuery({
    queryKey: [KEY, "summary", from, to],
    queryFn: () => api.summary(from, to),
    enabled,
    placeholderData: keepPreviousData,
    retry: 1,
  });

export const useTowTrucks = (enabled = true) =>
  useQuery({ queryKey: [KEY, "tow-trucks"], queryFn: api.listTowTrucks, enabled, retry: 1 });

export const useAssistanceRequests = (from: string, to: string, status?: RequestStatus, enabled = true) =>
  useQuery({
    queryKey: [KEY, "requests", from, to, status ?? "all"],
    queryFn: () => api.listRequests({ from, to, status }),
    enabled,
    placeholderData: keepPreviousData,
    retry: 1,
  });

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [KEY], predicate: (q) => q.queryKey[1] !== "access" });
}

export function useSaveTowTruck() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: TowTruckInput }) =>
      id ? api.updateTowTruck(id, input) : api.createTowTruck(input),
    onSuccess: invalidate,
  });
}

export function useDeleteTowTruck() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api.deleteTowTruck(id), onSuccess: invalidate });
}

export function useUpdateAssistanceRequest() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRequestInput }) => api.updateRequest(id, input),
    onSuccess: invalidate,
  });
}

export const useAssistanceClients = (enabled = true) =>
  useQuery({ queryKey: [KEY, "clients"], queryFn: api.listClients, enabled, retry: 1 });

export function useImportAssistanceClients() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ rows, dryRun }: { rows: ImportClientRow[]; dryRun: boolean }) => api.importClients(rows, dryRun),
    onSuccess: (_d, v) => {
      if (!v.dryRun) invalidate();
    },
  });
}

export function useRemoveAssistanceVehicle() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: string) => api.removeClientVehicle(id), onSuccess: invalidate });
}

export const useAssistanceTemplates = (enabled = true) =>
  useQuery({ queryKey: [KEY, "settings"], queryFn: api.settings, enabled, retry: 1 });

export function useSaveAssistanceTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.saveSettings,
    onSuccess: (data) => qc.setQueryData([KEY, "settings"], data),
  });
}

// Lista dos guinchos acionados + respostas no WhatsApp — atualiza sozinha
// enquanto a tela está aberta (posição do guincheiro, "respondeu").
export const useDispatches = (requestId: string | null) =>
  useQuery({
    queryKey: [KEY, "dispatches", requestId],
    queryFn: () => api.listDispatches(requestId!),
    enabled: !!requestId,
    refetchInterval: 8_000,
    retry: 1,
  });

export const useDispatchChat = (dispatchId: string | null) =>
  useQuery({
    queryKey: [KEY, "chat", dispatchId],
    queryFn: () => api.chat(dispatchId!),
    enabled: !!dispatchId,
    refetchInterval: 5_000,
    retry: 1,
  });

// Etiqueta por rastreador: { [deviceId]: { planName, groupName, source } }.
export const useAssistanceVehiclePlans = (enabled = true) =>
  useQuery({ queryKey: [KEY, "vehicle-plans"], queryFn: api.vehiclePlans, enabled, staleTime: 60_000, retry: false });

export function useCancelAssistanceRequest() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: ({ id, reason }: { id: string; reason: string | null }) => api.cancelRequest(id, reason), onSuccess: invalidate });
}
